import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@components/common/Button';
import { FormCurrencyField } from '@components/common/FormCurrencyField/FormCurrencyField';
import { IPurchaseForm } from './types';
import { ApplicationFormPageEnum } from '../types';
import { useNavigateBack } from '@hooks/useNavigateBack';
import { purchaseQuestions } from './questions';
import { getCurrentRoute } from '@utils/location';
import { useMutation } from '@tanstack/react-query';
import { applicationFormApi } from '@api/applicationForm/applicationFormApi';
import { useApplicationInProgressById } from '@queries/applications/useApplicationInProgressById';
import { useEffect } from 'react';
import { parseApplicationInProgress } from '../helpers';
import { extractFinancingAmountUpperLimitByFtv } from '../../CalculatorPage/FinanceDetailsPage/helpers';
import { countFtvValue, updateError } from './helpers';
import {
  MAX_DEPOSIT_TO_PROPERTY_PRICE_RATIO,
  MIN_DEPOSIT_TO_PROPERTY_PRICE_RATIO,
} from '../../CalculatorPage/criteria';

export const Purchase = () => {
  const intl = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigateBack = useNavigateBack();
  const { id: applicationId = '' } = useParams();
  const currentRoute = getCurrentRoute();
  const { mutate: patchApplicationInProgress } = useMutation(applicationFormApi.patchApplicationInProgress);
  const { data: applicationInProgress } = useApplicationInProgressById(applicationId);
  const formattedResponse =
    applicationInProgress?.[currentRoute] && parseApplicationInProgress(applicationInProgress[currentRoute]);
  const requiredFieldErrorMessage = intl.formatMessage({ id: 'common.error.requiredField' });

  const validate = (value: string | number | null, max: number): string | true => {
    if (value !== null && value !== '') {
      if (Number(value) > max) return intl.formatMessage({ id: 'applicationForm.PURCHASE.financeAmount.maxError' });
    }

    return true;
  };

  const validateMinDepositToProperty = (propertyPrice: number, deposit: number) => {
    if (propertyPrice > 0 && deposit) {
      if (deposit < propertyPrice * MIN_DEPOSIT_TO_PROPERTY_PRICE_RATIO) {
        return intl.formatMessage(
          { id: 'applicationForm.PURCHASE.deposit.maxError' },
          { maxDepositPercentage: `${MAX_DEPOSIT_TO_PROPERTY_PRICE_RATIO * 100}%` },
        );
      }
    }
  };

  const form = useForm<IPurchaseForm>({
    defaultValues: {
      propertyPrice: '',
      financeAmount: '',
    },
  });

  const financeAmount = form.watch('financeAmount');
  const propertyPrice = form.watch('propertyPrice');

  useEffect(() => {
    const deposit = (Number(propertyPrice) || 0) - (Number(financeAmount) || 0);
    const ftv = countFtvValue(deposit, Number(financeAmount));

    updateError(form, 'financeAmount', validate(financeAmount, extractFinancingAmountUpperLimitByFtv(ftv)));
    updateError(form, 'financeAmount', validateMinDepositToProperty(Number(propertyPrice), deposit));
  }, [financeAmount, propertyPrice]);

  useEffect(() => {
    if (formattedResponse) form.reset(formattedResponse);
  }, [applicationInProgress]);

  const isAnyFieldWithError = Boolean(Object.values(form.formState.errors).length);

  const submitFormHandler = async (formData: IPurchaseForm) => {
    const questions = Object.entries(formData).map(([key, answer]) => ({
      question: intl.formatMessage({ id: purchaseQuestions[key].labelKey }),
      key,
      answer,
    }));

    await patchApplicationInProgress({ questions, currentRoute, applicationId });

    searchParams.set('section', ApplicationFormPageEnum.REGION);
    setSearchParams(searchParams);
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(submitFormHandler)}>
        <div className={'mt-8'}>
          <FormattedMessage id={purchaseQuestions.propertyPrice.labelKey} tagName={'p'} />
          <FormCurrencyField
            name={'propertyPrice'}
            rules={{
              required: requiredFieldErrorMessage,
              validate: (value: string) => {
                if (value && Number(value) < 75000) {
                  return intl.formatMessage({ id: 'common.error.minNumber' }, { min: '£75,000' });
                }
                return true;
              },
            }}
          />
        </div>
        <div className={'mt-8'}>
          <FormattedMessage id={purchaseQuestions.financeAmount.labelKey} tagName={'p'} />
          <FormCurrencyField name={'financeAmount'} rules={{ required: requiredFieldErrorMessage }} />
        </div>

        <div className={'mt-8 flex justify-between'}>
          <Button variant={'outlined'} onClick={navigateBack}>
            <FormattedMessage id={'cta.back'} />
          </Button>

          <Button variant={'contained'} type={'submit'} disabled={isAnyFieldWithError}>
            <FormattedMessage id={'cta.continue'} />
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
